export type GuideRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type GuideArrow = {
  path: string;
  head: string;
  ring: GuideRect;
  nudgeX: number;
  nudgeY: number;
};

const RING_GAP = 5;
const TIP_GAP = 9;
const HEAD_SIZE = 11;
const SIDE_APPROACH_BELOW = 70;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const round = (value: number) => value.toFixed(1);

export const computeGuideArrow = (
  stageWidth: number,
  stageHeight: number,
  target: GuideRect
): GuideArrow => {
  const right = target.left + target.width;
  const bottom = target.top + target.height;
  const spaceAbove = target.top;
  const spaceBelow = stageHeight - bottom;
  const spaceLeft = target.left;
  const spaceRight = stageWidth - right;
  const verticalRoom = Math.max(spaceAbove, spaceBelow);
  const horizontalRoom = Math.max(spaceLeft, spaceRight);
  const fromSide =
    verticalRoom < SIDE_APPROACH_BELOW && horizontalRoom > verticalRoom;

  let tipX: number;
  let tipY: number;
  let startX: number;
  let startY: number;
  let side: number;

  if (fromSide) {
    const fromLeft = spaceLeft >= spaceRight;
    const length = clamp(horizontalRoom - 14, 36, 110);
    side = fromLeft ? 1 : -1;
    tipX = fromLeft ? target.left - TIP_GAP : right + TIP_GAP;
    tipY = target.top + target.height / 2;
    startX = clamp(
      fromLeft ? tipX - length : tipX + length,
      10,
      stageWidth - 10
    );
    startY = clamp(
      tipY + (tipY > stageHeight / 2 ? -40 : 40),
      10,
      stageHeight - 10
    );
  } else {
    const isWide = target.width > stageWidth * 0.55;
    tipX = isWide
      ? target.left + Math.min(60, target.width / 4)
      : target.left + target.width / 2;
    side = tipX > stageWidth / 2 ? -1 : 1;

    let fromAbove = spaceAbove >= spaceBelow;
    tipY = fromAbove ? target.top - TIP_GAP : bottom + TIP_GAP;
    const length = clamp((fromAbove ? spaceAbove : spaceBelow) - 14, 36, 90);
    startX = clamp(
      tipX + side * Math.min(110, length * 1.1),
      14,
      stageWidth - 14
    );
    startY = clamp(
      fromAbove ? tipY - length : tipY + length,
      10,
      stageHeight - 10
    );

    if (Math.abs(startY - tipY) < 24) {
      fromAbove = !fromAbove;
      tipY = fromAbove ? target.top - TIP_GAP : bottom + TIP_GAP;
      startY = clamp(fromAbove ? tipY - 60 : tipY + 60, 10, stageHeight - 10);
    }
  }

  const deltaX = tipX - startX;
  const deltaY = tipY - startY;
  const distance = Math.hypot(deltaX, deltaY) || 1;
  const bend = side * 0.28 * distance;
  const controlX = (startX + tipX) / 2 + (-deltaY / distance) * bend;
  const controlY = (startY + tipY) / 2 + (deltaX / distance) * bend;
  const tangent = Math.hypot(tipX - controlX, tipY - controlY) || 1;
  const unitX = (tipX - controlX) / tangent;
  const unitY = (tipY - controlY) / tangent;
  const baseX = tipX - unitX * HEAD_SIZE * 1.5;
  const baseY = tipY - unitY * HEAD_SIZE * 1.5;

  return {
    path: `M${round(startX)} ${round(startY)} Q${round(controlX)} ${round(
      controlY
    )} ${round(tipX - unitX * 8)} ${round(tipY - unitY * 8)}`,
    head: `M${round(tipX)} ${round(tipY)} L${round(
      baseX - unitY * HEAD_SIZE
    )} ${round(baseY + unitX * HEAD_SIZE)} L${round(
      baseX + unitY * HEAD_SIZE
    )} ${round(baseY - unitX * HEAD_SIZE)} Z`,
    ring: {
      left: target.left - RING_GAP,
      top: target.top - RING_GAP,
      width: target.width + RING_GAP * 2,
      height: target.height + RING_GAP * 2,
    },
    nudgeX: unitX * 6,
    nudgeY: unitY * 6,
  };
};
